import { useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, Filter, ChevronDown, X, LayoutGrid, List } from "lucide-react";
import {
  Button,
  Card,
  CardContent,
  Input,
  Select,
  type SelectOption,
} from "@/components/ui";
import { VehicleCard } from "@/components/vehicles";
import { cn } from "@/utils/format";
import { getVehicleSummaries, getMakes } from "@/data/vehicles";
import { useSeo } from "@/lib/seo";

const makeOptions: SelectOption[] = [
  { value: "", label: "All Makes" },
  ...getMakes().map((make) => ({ value: make.toLowerCase(), label: make })),
];

const bodyTypeOptions: SelectOption[] = [
  { value: "", label: "All Body Types" },
  { value: "sedan", label: "Sedan" },
  { value: "suv", label: "SUV" },
  { value: "truck", label: "Truck" },
  { value: "coupe", label: "Coupe" },
  { value: "hatchback", label: "Hatchback" },
];

const fuelTypeOptions: SelectOption[] = [
  { value: "", label: "All Fuel Types" },
  { value: "gasoline", label: "Gasoline" },
  { value: "electric", label: "Electric" },
  { value: "hybrid", label: "Hybrid" },
];

const sortOptions: SelectOption[] = [
  { value: "newest", label: "Newest Listed" },
  { value: "price_asc", label: "Price: Low to High" },
  { value: "price_desc", label: "Price: High to Low" },
  { value: "mileage_asc", label: "Mileage: Low to High" },
  { value: "year_desc", label: "Year: New to Old" },
];

export function VehiclesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState(searchParams.get("q") || "");
  const [showFilters, setShowFilters] = useState(false);
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");

  // Filter state
  const [filters, setFilters] = useState({
    make: searchParams.get("make") || "",
    bodyType: searchParams.get("bodyType") || "",
    fuelType: searchParams.get("fuelType") || "",
    maxPrice: searchParams.get("maxPrice") || "",
    maxMileage: searchParams.get("maxMileage") || "",
    certified: searchParams.get("certified") === "true",
    sortBy: searchParams.get("sortBy") || "newest",
  });

  const allVehicles = getVehicleSummaries();

  // Filter and sort vehicles
  const filteredVehicles = useMemo(() => {
    let results = allVehicles.filter(
      (v) => v.status === "available" || v.status === "pending",
    );

    // Search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      results = results.filter(
        (v) =>
          v.make.toLowerCase().includes(query) ||
          v.model.toLowerCase().includes(query) ||
          v.trim.toLowerCase().includes(query),
      );
    }

    // Make filter
    if (filters.make) {
      results = results.filter((v) => v.make.toLowerCase() === filters.make);
    }

    // Body type filter
    if (filters.bodyType) {
      results = results.filter((v) => {
        const vehicleData = allVehicles.find((av) => av.id === v.id);
        return (
          vehicleData && (vehicleData as any).bodyType === filters.bodyType
        );
      });
    }

    // Fuel type filter
    if (filters.fuelType) {
      results = results.filter((v) => v.fuelType === filters.fuelType);
    }

    // Max price filter
    if (filters.maxPrice) {
      const maxPriceCents = parseInt(filters.maxPrice) * 100;
      results = results.filter((v) => v.priceCents <= maxPriceCents);
    }

    // Max mileage filter
    if (filters.maxMileage) {
      results = results.filter(
        (v) => v.mileage <= parseInt(filters.maxMileage),
      );
    }

    // Certified filter
    if (filters.certified) {
      results = results.filter((v) => v.certified);
    }

    // Sort
    switch (filters.sortBy) {
      case "price_asc":
        results.sort((a, b) => a.priceCents - b.priceCents);
        break;
      case "price_desc":
        results.sort((a, b) => b.priceCents - a.priceCents);
        break;
      case "mileage_asc":
        results.sort((a, b) => a.mileage - b.mileage);
        break;
      case "year_desc":
        results.sort((a, b) => b.year - a.year);
        break;
      default:
        // newest - no sorting needed, already sorted by date in data
        break;
    }

    return results;
  }, [allVehicles, searchQuery, filters]);

  const clearFilters = () => {
    setSearchQuery("");
    setFilters({
      make: "",
      bodyType: "",
      fuelType: "",
      maxPrice: "",
      maxMileage: "",
      certified: false,
      sortBy: "newest",
    });
    setSearchParams({});
  };

  const hasActiveFilters =
    searchQuery ||
    filters.make ||
    filters.bodyType ||
    filters.fuelType ||
    filters.maxPrice ||
    filters.maxMileage ||
    filters.certified;

  useSeo({
    title: "Browse Vehicles | BESPOKIAN | Auto",
    description:
      "Search our certified pre-owned inventory. Filter by make, body type, fuel type, price, and mileage.",
    image: "/home-images/quick-actions/search-inventory.jpg",
  });

  return (
    <div className="min-h-screen bg-white">
      {/* Hero (match BrandPage / ShopSlugPage) */}
      <header className="relative min-h-[70vh] flex items-start justify-center pt-28 md:pt-32 pb-16 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat bg-gray-900"
          style={{
            backgroundImage: `url('/home-images/heroes/search-inventory.webp')`,
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/20 to-black/60" />
        <div className="absolute inset-0 bg-black/35" />

        <div className="relative z-10 w-full">
          <div className="text-center px-6 max-w-4xl mx-auto">
            <div className="mb-8">
              <h1 className="font-display text-5xl md:text-6xl text-white mb-4 leading-tight tracking-tight">
                Browse Vehicles
              </h1>
              <p className="text-white text-lg md:text-xl max-w-2xl mx-auto">
                <span className="font-medium">{filteredVehicles.length}</span>{" "}
                vehicles available
              </p>
              <div className="w-24 h-px bg-white mx-auto mt-6" />
            </div>
          </div>
        </div>
      </header>

      <div className="bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Search and Controls */}
          <div className="mb-6 space-y-4">
            <div className="flex flex-col sm:flex-row gap-4">
              {/* Search */}
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                <Input
                  type="search"
                  placeholder="Search by make, model, or keyword..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>

              {/* Filter toggle */}
              <Button
                variant="outline"
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center gap-2"
              >
                <Filter className="h-4 w-4" />
                Filters
                {hasActiveFilters && (
                  <span className="bg-primary-600 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                    !
                  </span>
                )}
                <ChevronDown
                  className={cn(
                    "h-4 w-4 transition-transform",
                    showFilters && "rotate-180",
                  )}
                />
              </Button>

              {/* View toggle */}
              <div className="hidden sm:flex border border-gray-300 rounded-lg overflow-hidden">
                <button
                  onClick={() => setViewMode("grid")}
                  className={cn(
                    "p-2 transition-colors",
                    viewMode === "grid"
                      ? "bg-primary-600 text-white"
                      : "bg-white text-gray-600 hover:bg-gray-50",
                  )}
                >
                  <LayoutGrid className="h-5 w-5" />
                </button>
                <button
                  onClick={() => setViewMode("list")}
                  className={cn(
                    "p-2 transition-colors",
                    viewMode === "list"
                      ? "bg-primary-600 text-white"
                      : "bg-white text-gray-600 hover:bg-gray-50",
                  )}
                >
                  <List className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Sort dropdown (always visible) */}
            <div className="flex items-center justify-between">
              <div className="w-48">
                <Select
                  options={sortOptions}
                  value={filters.sortBy}
                  onChange={(e) =>
                    setFilters({ ...filters, sortBy: e.target.value })
                  }
                />
              </div>
              {hasActiveFilters && (
                <Button
                  variant="ghost"
                  onClick={clearFilters}
                  className="text-sm"
                >
                  <X className="h-4 w-4 mr-1" />
                  Clear all filters
                </Button>
              )}
            </div>
          </div>

          {/* Expanded Filters */}
          {showFilters && (
            <Card className="mb-8 animate-slide-down">
              <CardContent className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <Select
                  label="Make"
                  options={makeOptions}
                  value={filters.make}
                  onChange={(e) =>
                    setFilters({ ...filters, make: e.target.value })
                  }
                />
                <Select
                  label="Body Type"
                  options={bodyTypeOptions}
                  value={filters.bodyType}
                  onChange={(e) =>
                    setFilters({ ...filters, bodyType: e.target.value })
                  }
                />
                <Select
                  label="Fuel Type"
                  options={fuelTypeOptions}
                  value={filters.fuelType}
                  onChange={(e) =>
                    setFilters({ ...filters, fuelType: e.target.value })
                  }
                />
                <Input
                  label="Max Price ($)"
                  type="number"
                  placeholder="e.g., 50000"
                  value={filters.maxPrice}
                  onChange={(e) =>
                    setFilters({ ...filters, maxPrice: e.target.value })
                  }
                />
                <Input
                  label="Max Mileage"
                  type="number"
                  placeholder="e.g., 50000"
                  value={filters.maxMileage}
                  onChange={(e) =>
                    setFilters({ ...filters, maxMileage: e.target.value })
                  }
                />
                <div className="flex items-end">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={filters.certified}
                      onChange={(e) =>
                        setFilters({ ...filters, certified: e.target.checked })
                      }
                      className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="text-sm font-medium text-gray-700">
                      Certified Only
                    </span>
                  </label>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Vehicle Grid */}
          {filteredVehicles.length > 0 ? (
            <div
              className={cn(
                "grid gap-6",
                viewMode === "grid"
                  ? "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
                  : "grid-cols-1",
              )}
            >
              {filteredVehicles.map((vehicle) => (
                <VehicleCard
                  key={vehicle.id}
                  vehicle={vehicle}
                  showStatus={vehicle.status !== "available"}
                  className={viewMode === "list" ? "flex-row" : ""}
                />
              ))}
            </div>
          ) : (
            /* Empty State */
            <div className="text-center py-16">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 mb-4">
                <Search className="h-8 w-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-900">
                No vehicles found
              </h3>
              <p className="mt-2 text-gray-600">
                Try adjusting your search or filter criteria.
              </p>
              <Button variant="outline" className="mt-4" onClick={clearFilters}>
                Clear All Filters
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
